import React, { useEffect, useMemo, useState } from 'react'
import { Select, Typography, Divider, Spin } from 'antd'
import { WalletOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

import IntlMessages from '../../util/IntlMessages'
import * as logger from '../../services/logger'
import { calculateSeniorityPreview } from '../../util/seniority-entitlement-preview'

import { QuotaUnits } from '@vacationtracker/shared/types/leave-policy'
import { AwardSeniorityEntitlementDaysOn, SeniorityEntitlementCalculateFrom } from '@vacationtracker/shared/types/automations'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '@vacationtracker/shared/data/app-parameters'

const { Text } = Typography

export interface ISeniorityPreviewEmployee {
  id: string
  name: string
  startDate: string
  location?: { name?: string }
}

export type UserQuotaByLeaveType = Record<string, { days: number; hours: number }>

interface ISeniorityEntitlementPreviewProps {
  employees: ISeniorityPreviewEmployee[]
  periods: { years: number; amount: number; units: QuotaUnits }[]
  units: QuotaUnits
  calculateFrom?: SeniorityEntitlementCalculateFrom
  awardSeniorityEntitlementDaysOn?: AwardSeniorityEntitlementDaysOn
  prorate?: boolean
  leaveTypeName?: string
  leaveTypeId?: string
  location?: { name?: string; resetQuotas?: string; yearStartMonth?: number; yearStartDay?: number }
  // Injected by the form so this component stays free of GraphQL (keeps it unit-testable).
  fetchUserQuota?: (userId: string) => Promise<UserQuotaByLeaveType>
}

const AVATAR_COLORS = ['#2b7fd4', '#1f9254', '#cc8400', '#7c4dff', '#c84141', '#0d9488']

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const avatarColor = (id: string) => {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const Avatar = ({ id, name }: { id: string; name: string }) => (
  <span className="sbe-avatar" style={{ backgroundColor: avatarColor(id) }}>
    {initials(name)}
  </span>
)

const SeniorityEntitlementPreview = ({
  employees,
  periods,
  units,
  calculateFrom,
  awardSeniorityEntitlementDaysOn,
  prorate,
  leaveTypeName,
  leaveTypeId,
  location,
  fetchUserQuota,
}: ISeniorityEntitlementPreviewProps) => {
  const { formatMessage } = useIntl()
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(employees[0]?.id)
  const [quotaByLeaveType, setQuotaByLeaveType] = useState<UserQuotaByLeaveType | null>(null)
  const [quotaLoading, setQuotaLoading] = useState(false)

  useEffect(() => {
    if (!employees.some((employee) => employee.id === selectedEmployeeId)) {
      setSelectedEmployeeId(employees[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees.map((employee) => employee.id).join(',')])

  const selectedEmployee = employees.find((employee) => employee.id === selectedEmployeeId)

  // Fetch the picked employee's quota once per employee (expensive → one user at a time).
  useEffect(() => {
    if (!fetchUserQuota || !selectedEmployeeId) {
      setQuotaByLeaveType(null)
      return
    }
    let cancelled = false
    setQuotaLoading(true)
    setQuotaByLeaveType(null)
    fetchUserQuota(selectedEmployeeId)
      .then((quota) => {
        if (!cancelled) setQuotaByLeaveType(quota)
      })
      .catch((err) => {
        if (!cancelled) {
          setQuotaByLeaveType(null)
          logger.error('ERROR FETCH PREVIEW QUOTA', err)
        }
      })
      .finally(() => {
        if (!cancelled) setQuotaLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId])

  const preview = useMemo(() => {
    if (!selectedEmployee || periods.length === 0) return null
    return calculateSeniorityPreview({
      startDate: selectedEmployee.startDate,
      now: new Date(),
      periods,
      calculateFrom,
      awardSeniorityEntitlementDaysOn,
      prorate,
      resetQuotas: location?.resetQuotas,
      yearStartMonth: location?.yearStartMonth,
      yearStartDay: location?.yearStartDay,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee?.startDate, JSON.stringify(periods), calculateFrom, awardSeniorityEntitlementDaysOn, prorate, location])

  const formatAmount = (amount: number) =>
    formatMessage({ id: units === 'hours' ? 'components.seniorityEntitlement.amountHours' : 'components.seniorityEntitlement.amountDays' }, { amount })
  const formatPlain = (amount: number) =>
    formatMessage({ id: units === 'hours' ? 'components.seniorityEntitlement.amountHoursPlain' : 'components.seniorityEntitlement.amountDaysPlain' }, { amount })
  const formatPerYear = (amount: number) =>
    formatMessage({ id: units === 'hours' ? 'components.seniorityEntitlement.perYearHours' : 'components.seniorityEntitlement.perYearDays' }, { amount })

  const round1 = (value: number) => Math.round(value * 10) / 10
  const leaveTypeQuota = leaveTypeId ? quotaByLeaveType?.[leaveTypeId] : undefined
  // baseQuota is expressed in the policy's own unit - one of days/hours is populated, the other 0.
  // Convert to the automation's unit so the current allowance and the added amount are consistent.
  const baseAmount = leaveTypeQuota
    ? round1(
        units === 'hours' ? leaveTypeQuota.hours || leaveTypeQuota.days * DEFAULT_WORKING_HOURS_IN_DAY : leaveTypeQuota.days || leaveTypeQuota.hours / DEFAULT_WORKING_HOURS_IN_DAY
      )
    : undefined
  const showBalance = Boolean(fetchUserQuota) && Boolean(leaveTypeName) && (quotaLoading || baseAmount !== undefined)

  const annDayLabel = selectedEmployee ? dayjs(selectedEmployee.startDate.split('T')[0]).format('D MMM') : ''
  const fiscalStartLabel =
    typeof location?.yearStartMonth === 'number' && typeof location?.yearStartDay === 'number'
      ? dayjs()
          .month(location.yearStartMonth - 1)
          .date(location.yearStartDay)
          .format('D MMM')
      : ''

  const footnote = () => {
    if (calculateFrom === 'TODAY') return <IntlMessages id="components.seniorityEntitlement.footnoteFromToday" />
    if (awardSeniorityEntitlementDaysOn === 'BEGINNING_OF_THE_FISCAL_YEAR') return <IntlMessages id="components.seniorityEntitlement.footnoteFiscalYear" />
    if (prorate && preview?.tiers.some((tier) => tier.split)) return <IntlMessages id="components.seniorityEntitlement.footnoteSplit" />
    return <IntlMessages id="components.seniorityEntitlement.footnoteAnniversary" />
  }

  return (
    <div className="sbe-preview-card">
      <div className="sbe-preview-title">
        <IntlMessages id="components.seniorityEntitlement.previewTitle" />
      </div>

      <div className="sbe-preview-label">
        <IntlMessages id="components.seniorityEntitlement.pickAnEmployee" />
      </div>
      <Select
        showSearch
        allowClear
        value={selectedEmployeeId}
        placeholder={formatMessage({ id: 'components.seniorityEntitlement.searchEmployees' })}
        style={{ width: '100%', marginBottom: 16 }}
        onChange={(value) => setSelectedEmployeeId(value)}
        filterOption={(input, option) =>
          String(option?.name ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        options={employees.map((employee) => ({ value: employee.id, name: employee.name }))}
        optionRender={(option) => (
          <span className="sbe-employee-option">
            <Avatar id={String(option.value)} name={option.data.name} />
            {option.data.name}
          </span>
        )}
        labelRender={(props) => {
          const employee = employees.find((item) => item.id === props.value)
          return employee ? (
            <span className="sbe-employee-option">
              <Avatar id={employee.id} name={employee.name} />
              {employee.name}
            </span>
          ) : (
            props.label
          )
        }}
        notFoundContent={<IntlMessages id="components.seniorityEntitlement.previewNoEmployees" />}
      />

      {!selectedEmployee && (
        <div className="sbe-preview-empty">
          <IntlMessages id="components.seniorityEntitlement.previewNoEmployees" />
        </div>
      )}

      {selectedEmployee && !preview && (
        <div className="sbe-preview-empty">
          <IntlMessages id="components.seniorityEntitlement.previewSelectSchedule" />
        </div>
      )}

      {selectedEmployee && preview && (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            <IntlMessages
              id={leaveTypeName ? 'components.seniorityEntitlement.joinedSummary' : 'components.seniorityEntitlement.joinedSummaryNoLeaveType'}
              values={{
                date: dayjs(selectedEmployee.startDate.split('T')[0]).format('MMMM DD YYYY'),
                seniority: preview.seniorityYears.toFixed(1),
                leaveType: leaveTypeName,
                b: (...chunks) => <b>{chunks}</b>,
              }}
            />
          </Text>

          <div className="sbe-timeline">
            {preview.tiers.map((tier, index) => {
              const showSplit = Boolean(tier.split) && tier.status !== 'skipped' && (tier.split as { fiscal: number }).fiscal > 0
              return (
                <div className={`sbe-timeline-row sbe-timeline-${tier.status}`} key={index}>
                  <span className="sbe-timeline-dot" />
                  <div className="sbe-timeline-body">
                    <div className="sbe-timeline-head">
                      <span className="sbe-timeline-year">
                        <IntlMessages id="components.seniorityEntitlement.yearMark" values={{ year: tier.years }} />
                      </span>
                      <span className={`sbe-timeline-status sbe-timeline-status-${tier.status}`}>
                        {tier.status === 'skipped' ? (
                          <IntlMessages id="components.seniorityEntitlement.tierIgnored" />
                        ) : (
                          <>
                            {formatAmount(tier.amount)}{' '}
                            <IntlMessages id={tier.status === 'credited' ? 'components.seniorityEntitlement.statusCredited' : 'components.seniorityEntitlement.statusUpcoming'} />
                          </>
                        )}
                      </span>
                    </div>
                    {showSplit && (
                      <div className="sbe-timeline-legs">
                        <div className="sbe-timeline-leg">
                          <IntlMessages id="components.seniorityEntitlement.legAnniversary" values={{ date: annDayLabel }} />
                          <b>{formatAmount((tier.split as { anniversary: number }).anniversary)}</b>
                        </div>
                        <div className="sbe-timeline-leg">
                          <IntlMessages id="components.seniorityEntitlement.legFiscalStart" values={{ date: fiscalStartLabel }} />
                          <b>{formatAmount((tier.split as { fiscal: number }).fiscal)}</b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="sbe-preview-summary">
            <div className="sbe-preview-breakdown-row">
              <span>
                <IntlMessages id="components.seniorityEntitlement.fromPastSeniority" />
              </span>
              <span>{formatAmount(preview.pastAmount)}</span>
            </div>
            <div className="sbe-preview-breakdown-row">
              <span>
                <IntlMessages id="components.seniorityEntitlement.futureTiers" />
              </span>
              <span>{formatAmount(preview.futureAmount)}</span>
            </div>
            <Divider style={{ margin: '10px 0' }} />
            <div className="sbe-preview-breakdown-row sbe-preview-total">
              <span>
                <IntlMessages id="components.seniorityEntitlement.totalEntitlement" />
              </span>
              <span>{formatAmount(preview.totalAmount)}</span>
            </div>
          </div>

          <Text type="secondary" className="sbe-preview-footnote">
            <ArrowRightOutlined className="sbe-preview-footnote-icon" />
            {footnote()}
          </Text>

          {showBalance && (
            <div className="sbe-balance">
              <Divider dashed style={{ margin: '16px 0' }} />
              <Spin spinning={quotaLoading}>
                <div className="sbe-balance-title">
                  <WalletOutlined />
                  <IntlMessages id="components.seniorityEntitlement.balanceTitle" values={{ leaveType: leaveTypeName }} />
                </div>
                <div className="sbe-balance-meta">
                  <IntlMessages
                    id="components.seniorityEntitlement.employeeMeta"
                    values={{
                      name: selectedEmployee.name,
                      location: selectedEmployee.location?.name ?? '',
                      seniority: preview.seniorityYears.toFixed(1),
                    }}
                  />
                </div>
                {baseAmount !== undefined && (
                  <>
                    <div className="sbe-balance-row">
                      <span>
                        <span className="sbe-balance-dot" />
                        <IntlMessages id="components.seniorityEntitlement.currentAllowance" values={{ leaveType: leaveTypeName }} />
                      </span>
                      <b>{formatPlain(baseAmount)}</b>
                    </div>
                    <div className="sbe-balance-row">
                      <span>
                        <ArrowRightOutlined className="sbe-balance-added-icon" />
                        <IntlMessages id="components.seniorityEntitlement.addedByRule" />
                      </span>
                      <b className={preview.totalAmount > 0 ? 'sbe-balance-added' : 'sbe-balance-added-zero'}>
                        {preview.totalAmount > 0 ? formatAmount(preview.totalAmount) : formatPlain(0)}
                      </b>
                    </div>
                    <div className="sbe-balance-total">
                      <span>
                        <IntlMessages id="components.seniorityEntitlement.newBalance" values={{ leaveType: leaveTypeName }} />
                      </span>
                      <b>{formatPerYear(round1(baseAmount + preview.totalAmount))}</b>
                    </div>
                  </>
                )}
              </Spin>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SeniorityEntitlementPreview
